import { ApiError, api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ─── Types (espejo del backend, Sprint 1 2026-05-16) ────────────────────────
// El modelo plano de 25 SEMANTIC_ACCOUNTS fue reemplazado por el árbol
// jerárquico (accounts_chart). Las cuentas del sistema tienen un `semanticKey`
// estable que los templates referencian. La UI itera el árbol real, no una
// lista hardcoded.

export const SEMANTIC_KEYS = [
  "BANCOS",
  "CUENTAS_POR_COBRAR",
  "TERNERAS_0_6",
  "TERNERAS_6_12",
  "NOVILLAS_PARA_INSEMINAR",
  "NOVILLAS_PRENADAS",
  "VACAS_PRODUCCION",
  "MACHOS_ENGORDE",
  "DEPRECIACION_ACUMULADA",
  "ANIMALES_PARA_VENTA",
  "CUENTAS_POR_PAGAR",
  "APORTES_SOCIOS",
  "UTILIDADES_PERIODO",
  "UTILIDADES_ACUMULADAS",
  "GANANCIA_VENTA_ANIMALES",
  "ALIMENTACION_POST_PROD",
  "SANIDAD_POST_PROD",
  "MANO_OBRA_POST_PROD",
  "OTROS_GASTOS_OP",
  "GASTO_DEPRECIACION",
  "MORTALIDAD_INMADURA",
  "MORTALIDAD_PRODUCTIVA",
  "PERDIDA_VENTA",
  "ALIMENTACION_CRIANZA",
  "SANIDAD_CRIANZA",
  "MANO_OBRA_CRIANZA",
  "OTROS_COSTOS_CRIANZA",
] as const;

export type SemanticKey = (typeof SEMANTIC_KEYS)[number];

export const EVENT_TYPE_LABELS: Record<string, string> = {
  animal_purchased: "Compra de animal",
  lot_cost_allocated: "Costo a lote",
  stage_transitioned: "Transición de etapa",
  first_calving_recorded: "Primer parto",
  depreciation_accrued: "Depreciación mensual",
  animal_died: "Mortalidad",
  animal_culled: "Descarte para venta",
  animal_sold: "Venta de animal",
};

// ─── Mappings (overrides del cliente sobre el árbol) ────────────────────────

export type ChartMappingRow = {
  accountChartId: string;
  semanticKey: SemanticKey | null;
  isSystem: boolean;
  defaultCode: string;
  defaultName: string;
  overrideId: string | null;
  overrideCode: string | null;
  overrideName: string | null;
};

export type MappingsListResponse = {
  mappings: ChartMappingRow[];
};

export type SeedMapping = {
  semanticKey: SemanticKey;
  code: string;
  name: string;
};

export type SeedResponse = { mappings: SeedMapping[] };

export type ApplySeedResult = { created: number; updated: number; skipped: number };

// ─── Preview ────────────────────────────────────────────────────────────────

export type PreviewLine = {
  account: {
    chartId: string;
    semanticKey: SemanticKey;
    code: string;
    name: string;
  };
  debit: string;
  credit: string;
};

export type PreviewEntry = {
  eventId: string;
  eventType: string;
  occurredAt: string;
  memo: string;
  lines: PreviewLine[];
};

export type PreviewResponse = {
  period: string;
  ready: true;
  missingSystemAccounts: never[];
  entries: PreviewEntry[];
  totals: { debits: string; credits: string };
};

export type PreviewMissingResponse = {
  code: "SYSTEM_ACCOUNTS_MISSING";
  message: string;
  period: string;
  missingSystemAccounts: SemanticKey[];
};

// ─── Query keys ─────────────────────────────────────────────────────────────

export const mappingsKey = (accountId: string) => ["mappings", accountId] as const;
export const seedKey = (accountId: string) => ["mappings", accountId, "seed"] as const;
export const previewKey = (accountId: string, period: string) =>
  ["journal", accountId, "preview", period] as const;

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useMappingsList(accountId: string) {
  return useQuery({
    queryKey: mappingsKey(accountId),
    queryFn: () => api.get<MappingsListResponse>(`/accounts/${accountId}/mappings`),
    enabled: Boolean(accountId),
    staleTime: 60_000,
  });
}

export function useSeedMappings(accountId: string, enabled = true) {
  return useQuery({
    queryKey: seedKey(accountId),
    queryFn: () => api.get<SeedResponse>(`/accounts/${accountId}/mappings/seed`),
    enabled: Boolean(accountId) && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useUpsertMapping(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { accountChartId: string; code: string; name: string }) =>
      api.put<{
        id: string;
        accountId: string;
        accountChartId: string;
        code: string;
        name: string;
      }>(`/accounts/${accountId}/mappings/${input.accountChartId}`, {
        code: input.code,
        name: input.name,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
      void qc.invalidateQueries({ queryKey: ["journal", accountId] });
    },
  });
}

export function useApplySeed(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mappings: SeedMapping[]; force?: boolean }) =>
      api.post<ApplySeedResult>(`/accounts/${accountId}/mappings/seed/apply`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
      void qc.invalidateQueries({ queryKey: ["journal", accountId] });
    },
  });
}

export function useDeleteMapping(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountChartId: string) =>
      api.delete<void>(`/accounts/${accountId}/mappings/${accountChartId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mappingsKey(accountId) });
    },
  });
}

// El preview puede devolver 422 con missingSystemAccounts — ese caso se maneja
// en el page leyendo error.details.
export type PreviewQueryResult =
  | { kind: "ready"; data: PreviewResponse }
  | { kind: "missing"; missing: SemanticKey[]; period: string };

// ─── Slice 6: exports ───────────────────────────────────────────────────────

export type JournalExportSummary = {
  id: string;
  periodStart: string;
  periodEnd: string;
  exportedAt: string;
  exportedBy: string;
  contentHash: string;
  eventCount: number;
  adjustmentCount: number;
  fileKey: string;
};

export type ListedJournalExport = {
  id: string;
  periodStart: string;
  periodEnd: string;
  exportedAt: string;
  exportedBy: string;
  exportedByName: string | null;
  exportedByEmail: string | null;
  contentHash: string;
  eventCount: number;
  adjustmentCount: number;
};

export type ListedExportsResponse = {
  exports: ListedJournalExport[];
};

export const exportsKey = (accountId: string) => ["journal", accountId, "exports"] as const;

export function useExportsList(accountId: string, enabled = true) {
  return useQuery({
    queryKey: exportsKey(accountId),
    queryFn: () => api.get<ListedExportsResponse>(`/accounts/${accountId}/journal/exports`),
    enabled: Boolean(accountId) && enabled,
    staleTime: 30_000,
  });
}

export function useCreateExport(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { periodStart: string; periodEnd: string }) =>
      api.post<JournalExportSummary>(`/accounts/${accountId}/journal/exports`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: exportsKey(accountId) });
    },
  });
}

export async function downloadExportFile(
  accountId: string,
  exportId: string,
  filename: string,
): Promise<void> {
  const { getIdToken } = await import("@/common/lib/auth");
  const token = await getIdToken(false);
  const res = await fetch(`/api/accounts/${accountId}/journal/exports/${exportId}/file`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, "DOWNLOAD_FAILED", `HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function usePreview(accountId: string, period: string) {
  return useQuery({
    queryKey: previewKey(accountId, period),
    queryFn: async (): Promise<PreviewQueryResult> => {
      try {
        const data = await api.get<PreviewResponse>(
          `/accounts/${accountId}/journal/preview?period=${period}`,
        );
        return { kind: "ready", data };
      } catch (e) {
        if (
          e instanceof ApiError &&
          e.status === 422 &&
          (e.code === "SYSTEM_ACCOUNTS_MISSING" || e.code === "MAPPINGS_INCOMPLETE")
        ) {
          const details = e.details as
            | { missingSystemAccounts?: SemanticKey[]; period?: string }
            | undefined;
          return {
            kind: "missing",
            missing: details?.missingSystemAccounts ?? [],
            period: details?.period ?? period,
          };
        }
        throw e;
      }
    },
    enabled: Boolean(accountId && period),
    staleTime: 30_000,
    retry: (count, e) => {
      if (e instanceof ApiError && (e.status === 422 || e.status === 400)) return false;
      return count < 2;
    },
  });
}
